<template>
    <test-layout>
        <div class="space-y-20">
            <div class="md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="px-4 sm:px-0">
                        <h3 class="text-lg font-medium leading-6 text-gray-900">Profile Information</h3>
                        <p class="text-gray-500">Your personal profile information will be used to populate your subscription documents.</p>
                    </div>
                </div>
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="shadow overflow-hidden sm:rounded-md">
                        <div class="px-4 py-5 bg-white space-y-6 sm:p-6">
                            <div v-for="(value, key) in primaryData" :key="key">
                                <h4 class="block text-base font-medium text-gray-500">{{ convertField(key) }}</h4>
                                <div v-if="key != 'documents'" class="mt-1 py-1 text-lg border-b border-gray-200">{{ convertValue(value) }}</div>
                                <div v-else class="mt-1">
                                    <app-downloadables :items="value"></app-downloadables>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="investingType == 'joint'" class="mt-6 shadow overflow-hidden sm:rounded-md">
                        <div class="px-4 py-5 bg-white space-y-6 sm:p-6">
                            <div v-for="(value, key) in secondaryData" :key="key">
                                <h4 class="block text-base font-medium text-gray-500">{{ convertField(key) }}</h4>
                                <div v-if="key != 'documents'" class="mt-1 py-1 text-lg border-b border-gray-200">{{ value }}</div>
                                <div v-else class="mt-1">
                                    <app-downloadables :items="value"></app-downloadables>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="investingType == 'entity'" class="mt-6 shadow overflow-hidden sm:rounded-md">
                        <div class="px-4 py-5 bg-white space-y-6 sm:p-6">
                            <div v-for="(value, key) in entityData" :key="key">
                                <div v-if="key != 'information' && key != 'beneficiary'">
                                    <h4 class="block text-base font-medium text-gray-500">{{ convertField(key) }}</h4>
                                    <div class="mt-1 py-1 text-lg border-b border-gray-200">{{ convertValue(value) }}</div>
                                </div>

                                <div v-if="key == 'information' && entityData.moreSignatures == 'No'">
                                    <h4 class="block text-base font-medium text-gray-500">{{ convertField(key) }}</h4>
                                    <div class="mt-1 text-sm text-gray-900">
                                        <ul class="border border-gray-200 rounded-md divide-y divide-gray-200">
                                            <li v-for="(info, ikey) in value" :key="ikey" class="pl-3 pr-4 py-3 text-sm">
                                                <h4 class="block text-base font-medium text-gray-500">{{ convertField(ikey) }}</h4>
                                                <div class="mt-1 text-lg">{{ convertValue(info) }}</div>
                                            </li>
                                        </ul>
                                    </div>
                                </div>

                                <div v-if="key == 'beneficiary'">
                                    <h4 class="block text-base font-medium text-gray-500">{{ convertField(key) }}</h4>
                                    <div class="mt-1 text-sm text-gray-900">
                                        <ul v-for="(beneficiary, bkey) in value" :key="bkey" class="border border-gray-200 rounded-md divide-y divide-gray-200">
                                            <li v-for="(info, ikey) in beneficiary" :key="ikey" class="pl-3 pr-4 py-3 text-sm">
                                                <h4 class="block text-base font-medium text-gray-500">{{ convertField(ikey) }}</h4>
                                                <div class="mt-1 text-lg">{{ convertValue(info) }}</div>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="investingType == 'ira'" class="mt-6 shadow overflow-hidden sm:rounded-md">
                        <div class="px-4 py-5 bg-white space-y-6 sm:p-6">
                            <div v-for="(value, key) in iraData" :key="key">
                                <div v-if="key != 'beneficiary'">
                                    <h4 class="block text-base font-medium text-gray-500">{{ convertField(key) }}</h4>
                                    <div class="mt-1 py-1 text-lg border-b border-gray-200">{{ convertValue(value) }}</div>
                                </div>

                                <div v-if="key == 'beneficiary'">
                                    <h4 class="block text-base font-medium text-gray-500">{{ convertField(key) }}</h4>
                                    <div class="mt-1 text-sm text-gray-900">
                                        <ul v-for="(beneficiary, bkey) in value" :key="bkey" class="border border-gray-200 rounded-md divide-y divide-gray-200">
                                            <li v-for="(info, ikey) in beneficiary" :key="ikey" class="pl-3 pr-4 py-3 text-sm">
                                                <h4 class="block text-base font-medium text-gray-500">{{ convertField(ikey) }}</h4>
                                                <div class="mt-1 text-lg">{{ convertValue(info) }}</div>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="px-4 sm:px-0">
                        <h3 class="text-lg font-medium leading-6 text-gray-900">Suitability Information</h3>
                        <p class="text-gray-500">This information is used to determine if it is reasonable to recommend this investment strategy to you.</p>
                    </div>
                </div>
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="shadow overflow-hidden sm:rounded-md">
                        <div class="px-4 py-5 bg-white space-y-6 sm:p-6">
                            <div v-for="(value, key) in intake.suitabilityForm" :key="key">
                                <h4 class="block text-base font-medium text-gray-500">{{ convertField(key) }}</h4>
                                <div class="mt-1 py-1 text-lg border-b border-gray-200">{{ convertValue(value) }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="px-4 sm:px-0">
                        <h3 class="text-lg font-medium leading-6 text-gray-900">Investment Information</h3>
                        <p class="text-gray-500">This is the financial information around your investment.</p>
                    </div>
                </div>
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="shadow overflow-hidden sm:rounded-md">
                        <div class="px-4 py-5 bg-white space-y-6 sm:p-6">
                            <div>
                                <h4 class="block text-base font-medium text-gray-500">Investing Type</h4>
                                <div class="mt-1 py-1 text-lg border-b border-gray-200">{{ convertValue(investingType) }}</div>
                            </div>

                            <div v-for="(value, key) in investData" :key="key">
                                <div v-if="key == 'distribution'">
                                    <h4 class="block text-base font-medium text-gray-500">{{ convertField(key) }}</h4>
                                    <div class="mt-1 text-sm text-gray-900">
                                        <ul class="border border-gray-200 rounded-md divide-y divide-gray-200">
                                            <li v-for="(info, ikey) in value" :key="ikey" class="pl-3 pr-4 py-3 text-sm">
                                                <h4 class="block text-base font-medium text-gray-500">{{ convertField(ikey) }}</h4>
                                                <div class="mt-1 text-lg">{{ convertValue(info) }}</div>
                                            </li>
                                        </ul>
                                    </div>
                                </div>

                                <div v-else-if="key == 'fund'">
                                    <h4 class="block text-base font-medium text-gray-500">{{ convertField(key) }}</h4>
                                    <div class="mt-1 text-sm text-gray-900">
                                        <ul v-if="value.which != 'yes'" class="border border-gray-200 rounded-md divide-y divide-gray-200">
                                            <template v-for="(info, ikey) in value" :key="ikey">
                                                <li v-if="ikey != 'which'" class="pl-3 pr-4 py-3 text-sm">
                                                    <h4 class="block text-base font-medium text-gray-500">{{ convertField(ikey) }}</h4>
                                                    <div class="mt-1 text-lg">{{ convertValue(info) }}</div>
                                                </li>
                                            </template>
                                        </ul>
                                        <div v-else class="mt-1 py-1 text-lg border-b border-gray-200">Sames as Distribution</div>
                                    </div>
                                </div>

                                <div v-else-if="key == 'fee'">
                                    <h4 class="block text-base font-medium text-gray-500">{{ convertField(key) }}</h4>
                                    <div class="mt-1 text-sm text-gray-900">
                                        <ul v-if="value.which != 'yes'" class="border border-gray-200 rounded-md divide-y divide-gray-200">
                                            <template v-for="(info, ikey) in value" :key="ikey">
                                                <li v-if="ikey != 'which'" class="pl-3 pr-4 py-3 text-sm">
                                                    <h4 class="block text-base font-medium text-gray-500">{{ convertField(ikey) }}</h4>
                                                    <div class="mt-1 text-lg">{{ convertValue(info) }}</div>
                                                </li>
                                            </template>
                                        </ul>
                                        <div v-else class="mt-1 py-1 text-lg border-b border-gray-200">Sames as Distribution</div>
                                    </div>
                                </div>

                                <div v-else>
                                    <h4 class="block text-base font-medium text-gray-500">{{ convertField(key) }}</h4>
                                    <div class="mt-1 py-1 text-lg border-b border-gray-200">{{ value || '&nbsp;' }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="px-4 sm:px-0">
                        <h3 class="text-lg font-medium leading-6 text-gray-900">Verification Information</h3>
                        <p class="text-gray-500">We verified your accreditation or qualification to invest with Mammoth based on this information.</p>
                    </div>
                </div>
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="shadow overflow-hidden sm:rounded-md">
                        <div class="px-4 py-5 bg-white space-y-6 sm:p-6">
                            <div v-for="(value, key) in intake.verificationForm" :key="key">
                                <h4 class="block text-base font-medium text-gray-500">{{ convertField(key) }}</h4>
                                <div class="mt-1">
                                    <app-downloadables v-if="key == 'documents'" :items="value"></app-downloadables>
                                    <div v-else class="mt-1 py-1 text-lg border-b border-gray-200">{{ value || '&nbsp;' }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </test-layout>
</template>

<script>
import TestLayout from '@/Layouts/TestLayout'
import AppDownloadables from '@Components/Downloadables'

export default {
    components: {
        TestLayout,
        AppDownloadables
    },
    props: ['intake'],
    methods: {
        convertField(field){
            const fields = {
                firstName: 'First Name',
                lastName: 'Last Name',
                middleInitial: 'Middle Initial',
                suffix: 'Suffix',
                phone: 'Phone',
                dob: 'Date of Birth',
                idType: 'ID Type',
                idNumber: 'ID Number',
                citizenship: 'Citizenship',
                country: 'Country',
                ssn: 'Social Security Number',
                city: 'City',
                address1: 'Address 1',
                address2: 'Address 2',
                state: 'State',
                zip: 'Zip',
                email: 'Email Address',
                financialAffiliation: 'Is Financial Firm Affiliated?',
                financialFirm: 'Financial Firm Name',
                financialFirmPosition: 'Financial Firm Position',
                publicCompanyAffiliation: 'Is Public Company Affiliated?',
                publicCompany: 'Public Company Name',
                publicCompanyPosition: 'Public Company Position',
                politicalAffiliation: 'Is Political Affiliated?',
                politicalCountry: 'Political Country',
                politicalRole: 'Political Role',
                annualIncome: 'Annual Income',
                netWorth: 'Net Worth',
                primaryIncome: 'Primary Income',
                spouseIncome: 'Spouse Income',
                liquidNetWorth: 'Liquid Net Worth',
                taxRate: 'Tax Rate',
                financialPortfolio: 'Financial Portfolio',
                sourceFunds: 'Source of Funds',
                partnerCode: 'Partner Code',
                employment: 'Employment Status',
                businessId: 'Business ID Number',
                taxId: 'Federal Tax ID Number',
                ein: 'Employment Identification Number',
                entityType: 'Entity Type',
                entityName: 'Entity Name',
                documentsType: 'Documents Type',
                documents: 'Documents',
                pctRealEstate: 'Real Estate',
                pctLiquidSecurities: 'Liquid Securities',
                pctPrivSecurities: 'Private Securities',
                timeHorizon: 'Time Horizon',
                riskProfile: 'Risk Profile',
                investmentObjective: 'Investment Objective',
                investmentExperience: 'Investment Experience',
                privOffExperience: 'Private Offerings',
                education: 'Education',
                financialAdvisor: 'Financial Advisor',
                advisorFirstName: 'Advisor First Name',
                advisorLastName: 'Advisor Last Name',
                advisorEmail: 'Advisor Email',
                advisorPhone: 'Advisor Phone',
                advisorAddress: 'Advisor Address',
                advisorFirm: 'Advisor Firm',
                includeAdvisorCommunication: 'Include Advisor Communication',
                trustedContact: 'Has Trusted Contact?',
                contactFirstName: 'Trusted Contact First Name',
                contactLastName: 'Trusted Contact Last Name',
                contactPhone: 'Trusted Contact Phone',
                contactEmail: 'Trusted Contact Email',
                contactRelation: 'Trusted Contact Relation',
                shares: 'Shares',
                amount: 'Amount',
                paymentMethod: 'Payment Method',
                capital_call_today: 'Capital Call Today',
                true_up_dollar: 'True Up Dollar',
                toEmailDocuments: 'To Email Documents',
                uploadLater: 'Upload Later?',
                ExtAccountfullname: 'Account Full Name',
                Extnickname: 'Nickname',
                ExtBankname: 'Bank Name',
                ExtRoutingnumber: 'Routing Number',
                ExtAccountnumber: 'Account Number',
                isAgree: 'Agree to the terms of the Mammoth Technology LLC',
                information: 'Information',
                moreSignatures: 'Are there more than two signatures required?',
                soleSignature: 'Do you have sole signature authority?',
                beneficiary: 'Beneficiary',
                hasAccount: 'Has Account?',
                custodianName: 'Custodian Name',
                accountNumber: 'Account Number',
                accountName: 'Account Name',
                ccName: 'Card Name',
                ccNumber: 'Card Number',
                ccExpiration: 'Card Expiration',
                ccVerificationCode: 'CVC Code',
            }

            return _.get(fields, field, _.capitalize(field))
        },
        convertValue(value){
            const values = {
                'less_than_100k': 'Less than $100,000',
                '100k_200k': '$100,000 - $200,000',
                '200k_300k': '$200,000 - $300,000',
                '300k_750k': '$300,000 - $750,000',
                '750k_plus': '$750,000 +',
                '100k_1m': '$100,000 - $1,000,000',
                '1m_2p2m': '$1,000,000 - $2,200,000',
                '2p2m_5m': '$2,200,000 - $5,000,000',
                '5m_plus': '$5,000,000 +',
                '100k_500k': '$100,000 - $500,000',
                '500k_1m': '$500,000 - $1,000,000',
                '1m_4m': '$1,000,000 - $4,000,000',
                '4m_plus': '$4,000,000 +',
                '15_or_less': '15% or less',
                '16_25': '16% - 25%',
                '26_35': '26% - 35%',
                '35_plus': '36%+',
                'less_10': 'Less than 10%',
                '11_20': '11% - 20%',
                '21_33': '21% - 33%',
                '34_67': '34% - 67%',
                '68_100': '68% - 100%',
                'individual': 'Individual',
                'entity': 'Entity',
                'joint-tenants-survivorship': 'Joint Tenants with Rights of Survivorship (most common)',
                'joint-tenants-common': 'Tenants in Common',
                'joint-tenants-entirety': 'Tenants by Entirety',
                'ira-traditional': 'Traditional IRA',
                'ira-roth': 'Roth IRA',
            }

            return _.get(values, value, value)
        }
    },
    computed: {
        investingType(){
            return (['joint', 'ira', 'other'].includes(this.intake.profileForm.investingType)) ? this.intake.profileForm.subInvestingType : this.intake.profileForm.investingType
        },
        primaryData(){
            return this.intake.profileForm.primary
        },
        secondaryData(){
            return this.intake.profileForm.secondary
        },
        entityData(){
            return this.intake.profileForm.entity
        },
        iraData(){
            return this.intake.profileForm.ira
        },
        investData(){
            return _.pickBy(this.intake.investForm, (value, key) => {
                return !['shares'].includes(key)
            })
        }
    }
}
</script>

<style>

</style>
